import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

import Layout from '../../components/Layout'
import { getBilling } from '../../api/account'
import { getUserInfo } from '../../api/auth'
import { setUser } from '../../redux/actions/user'
import ProfileSection from './ProfileSection'
import SubscriptionSection from './SubscriptionSection'
import BillingSection from './BillingSection'
import OrdersSection from './OrdersSection'
import GroupsSection from './GroupsSection'
import NotificationsSection from './NotificationsSection'
import SecuritySection from './SecuritySection'
import DeleteSection from './DeleteSection'

import styles from './AccountSettings.module.css'

const SECTIONS = [
  ['profile', 'Profile'],
  ['subscription', 'Subscription'],
  ['billing', 'Payment and billing'],
  ['orders', 'Cookbook orders'],
  ['groups', 'Groups and invites'],
  ['notifications', 'Email notifications'],
  ['security', 'Password and sign-in'],
  ['delete', 'Delete account'],
]

// Everything about a Morsel account on one page (replaces the old settings menu
// and Chargebee's portal)
const AccountSettings = () => {
  const user = useSelector((state) => state.user?.data)
  const { hash, search } = useLocation()
  const [billing, setBilling] = useState(null)
  const [billingError, setBillingError] = useState(false)
  const [active, setActive] = useState('profile')

  const dispatch = useDispatch()
  const loadBilling = () =>
    getBilling()
      .then(({ data }) => {
        setBilling(data)
        setBillingError(false)
        // Loading billing re-checks the card, which can start or end the expiring card
        // warning; pick that up so the banner follows
        getUserInfo()
          .then(({ data: fresh }) => dispatch(setUser(fresh)))
          .catch(() => null)
      })
      .catch(() => setBillingError(true))

  useEffect(() => {
    loadBilling()
  }, [search])

  // Jump to a section (/settings#groups) once the page has its full height
  const scrolled = useRef('')
  useEffect(() => {
    if (!hash || scrolled.current === hash || (!billing && !billingError)) return
    scrolled.current = hash
    const id = hash === '#create-group' ? 'groups' : hash.slice(1)
    setActive(id)
    // After the site's own scroll-to-top on page change
    setTimeout(() => document.getElementById(id)?.scrollIntoView(), 150)
  }, [hash, billing, billingError])

  // Highlight the section being read in the menu
  useEffect(() => {
    if (!window.IntersectionObserver) return
    const observer = new IntersectionObserver(
      (entries) => {
        const seen = entries.filter((e) => e.isIntersecting)
        if (seen.length) setActive(seen[0].target.id)
      },
      { rootMargin: '-20% 0px -70% 0px' }
    )
    SECTIONS.forEach(([id]) => {
      const el = document.getElementById(id)
      if (el) observer.observe(el)
    })
    return () => observer.disconnect()
  }, [user?.id, user?.cookbook_enabled])

  if (!user) return null

  return (
    <Layout title="Account">
      <div className={styles.page}>
        <nav aria-label="Account sections" className={styles.nav}>
          <h1 className={styles.title}>Account</h1>
          {SECTIONS.filter(([id]) => id !== 'orders' || user.cookbook_enabled).map(
            ([id, label]) => (
              <a
                key={id}
                href={`#${id}`}
                onClick={() => setActive(id)}
                className={`${styles.navLink} ${active === id ? styles.navActive : ''}`}
              >
                {label}
              </a>
            )
          )}
          <p className={styles.navNote}>Everything about your Morsel account, on one page.</p>
        </nav>
        <div className={styles.sections}>
          <ProfileSection user={user} />
          <SubscriptionSection billing={billing} failed={billingError} reload={loadBilling} />
          <BillingSection billing={billing} reload={loadBilling} />
          <OrdersSection />
          <GroupsSection />
          <NotificationsSection user={user} />
          <SecuritySection />
          <DeleteSection />
        </div>
      </div>
    </Layout>
  )
}

export default AccountSettings
